import { useCallback, useEffect, useState } from 'react';
import {
  ThemeMode,
  ResolvedTheme,
  getStoredTheme,
  getSystemTheme,
  resolveTheme,
  applyTheme,
  setThemeMode,
  subscribeTheme,
  watchSystemTheme,
} from '../services/theme';

export interface UseThemeResult {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

export function useTheme(): UseThemeResult {
  const [mode, setModeState] = useState<ThemeMode>(() => getStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(getStoredTheme()));

  useEffect(() => {
    applyTheme(getStoredTheme());
    const unsubscribe = subscribeTheme((next) => {
      setModeState(next);
      setResolved(resolveTheme(next));
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (mode !== 'system') return undefined;
    setResolved(getSystemTheme());
    return watchSystemTheme((theme) => {
      applyTheme('system');
      setResolved(theme);
    });
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setThemeMode(next);
  }, []);

  const toggle = useCallback(() => {
    setThemeMode(resolveTheme(getStoredTheme()) === 'dark' ? 'light' : 'dark');
  }, []);

  return {
    mode,
    resolved,
    isDark: resolved === 'dark',
    setMode,
    toggle,
  };
}
